import React, { useMemo, useLayoutEffect, useCallback } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { MultiList } from '@sas/nova-commons/components/ListBox/MultiList';
import { exposeTestId } from '../../config';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { CheckboxListState } from '../../store/birdElement/checkboxListState';
import { selectBirdCheckboxListState, selectStyleState, selectAccessibilityLabelState, } from '../../store/selectors';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { fontStyles, longestTextWidthForValues, ItemHeightPadding, textHeight, } from '../utils/style';
import { useFontLoadedKey } from '../FontProvider';
import { SearchableList } from '../SearchableList';
import { Link } from '@sas/nova-commons/components/Link';
import { useI18n } from '../../locale/I18nContext';
const checkboxListItemWidthPadding = 32 /*padding*/ + 2 /*border*/ + 24; /* checkbox width */
export const BirdCheckboxListBase = function (props) {
    const actions = props.actions;
    const { values, measureValues, selectedIndices, required, selectAllEnabled, searchable, } = props.state;
    const { normal, font } = props.style || {};
    const localize = useI18n();
    const combinedValues = useMemo(() => {
        if (!values) {
            return;
        }
        return values.map((value, index) => {
            return {
                text: value,
                secondaryText: measureValues ? measureValues[index] : undefined,
            };
        });
    }, [values, measureValues]);
    {
        const dt = useDesignTokens();
        const fonts = useMemo(() => fontStyles(font, dt), [font, dt]);
        const uiFonts = useMemo(() => fontStyles(undefined, dt), [dt]);
        const fontKey = useFontLoadedKey(fonts.fontFamily);
        const preferredValues = props.preferredSizing ? combinedValues : undefined;
        useLayoutEffect(() => {
            const itemHeight = Math.max(22, // checkbox height
            textHeight(fonts)) + ItemHeightPadding;
            const constraints = {
                minHeight: itemHeight * 2,
                minWidth: 160,
            };
            if (preferredValues) {
                constraints.preferredWidth =
                    longestTextWidthForValues(preferredValues, fonts) + checkboxListItemWidthPadding;
                constraints.preferredHeight = itemHeight * preferredValues.length;
                if (searchable) {
                    constraints.preferredHeight +=
                        Math.max(28, textHeight(uiFonts) + 4 /* padding */) +
                            1 /* input fudge factor */ +
                            2 /* border */ +
                            6 /* padding around */;
                }
                if (selectAllEnabled) {
                    constraints.preferredHeight += textHeight(uiFonts) + 3 /* padding-bottom */;
                }
            }
            actions.setConstraints(props.id, constraints);
        }, [
            fontKey /*(implicit)*/,
            preferredValues,
            actions,
            props.id,
            fonts,
            uiFonts,
            searchable,
            selectAllEnabled,
        ]);
    }
    const tokenOverrides = useCallback((cbProps) => {
        if (props.style && cbProps.state === 'normal') {
            return props.style.normal;
        }
    }, [props.style]);
    const selectedIndicesSet = useMemo(() => new Set(selectedIndices), [selectedIndices]);
    const onSelected = (nextSelectedIndices) => {
        if (required && nextSelectedIndices.size <= 0) {
            return;
        }
        actions.update(props.id, { selectedIndices: Array.from(nextSelectedIndices) });
    };
    const selectAllText = useMemo(() => {
        if (required) {
            if ((selectedIndices === null || selectedIndices === void 0 ? void 0 : selectedIndices.length) === (values === null || values === void 0 ? void 0 : values.length)) {
                return localize({ key: 'CheckboxList.clearSelection.txt' });
            }
            else {
                return localize({ key: 'CheckboxList.selectAll.txt' });
            }
        }
        if (selectedIndices && (selectedIndices === null || selectedIndices === void 0 ? void 0 : selectedIndices.length) > 0) {
            return localize({
                key: 'CheckboxList.clearSelection.fmt',
                args: { count: `${selectedIndices.length}` },
            });
        }
        else {
            return localize({ key: 'CheckboxList.selectAll.txt' });
        }
    }, [selectedIndices, required, values, localize]);
    const selectAll = () => {
        if (values) {
            if (required) {
                if ((selectedIndices === null || selectedIndices === void 0 ? void 0 : selectedIndices.length) === values.length) {
                    actions.update(props.id, {
                        selectedIndices: [0],
                    });
                }
                else {
                    actions.update(props.id, {
                        selectedIndices: [...Array(values.length).keys()],
                    });
                }
            }
            else {
                if (selectedIndices && selectedIndices.length > 0) {
                    actions.update(props.id, {
                        selectedIndices: undefined,
                    });
                }
                else {
                    actions.update(props.id, {
                        selectedIndices: [...Array(values.length).keys()],
                    });
                }
            }
        }
    };
    const renderList = (items, selectedIndices, onSelected) => {
        if (selectAllEnabled) {
            return (React.createElement("div", { style: { overflowY: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' } },
                React.createElement("div", { style: { padding: '0 0 0.1875rem 0.5625rem' } },
                    React.createElement(Link, { onPress: selectAll, wrapText: false }, selectAllText)),
                React.createElement(MultiList, { testId: exposeTestId() ? `${props.id}-CbList` : null, onSelected: onSelected, selectedIndices: selectedIndices, items: items, theme: tokenOverrides, font: font, style: {
                        overflowY: 'visible',
                        overflowX: 'hidden',
                        height: '100%',
                        backgroundColor: normal && normal.bg,
                    }, ariaLabel: props.accessibilityLabel })));
        }
        return (React.createElement(MultiList, { testId: exposeTestId() ? `${props.id}-CbList` : null, onSelected: onSelected, selectedIndices: selectedIndices, items: items, theme: tokenOverrides, font: font, style: {
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                backgroundColor: normal && normal.bg,
            }, ariaLabel: props.accessibilityLabel }));
    };
    return !searchable ? (renderList(combinedValues, selectedIndicesSet, onSelected)) : (React.createElement(SearchableList, { type: "multi", items: combinedValues || [], selection: selectedIndicesSet, onSelected: onSelected, renderList: renderList }));
};
export const BirdCheckboxList = connect((state, props) => ({
    state: selectBirdCheckboxListState(state, props.id),
    style: selectStyleState(state, props.id),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, CheckboxListState.actions), dispatch),
}))(BirdCheckboxListBase);
